import { useEffect } from 'react';
import { Gender } from '@/types/tournament.model';
import type { Category } from '@/types/tournament.model';
import type { WizardStepProps, TeamConfigSlice } from '../../../engine/types';
import {
  GenderField,
  AgeRestrictionField,
  PlayersPerTeamField,
} from '../../../blocks/team-config';
import { CategoryRangeField } from '../../../blocks/fixture-config';

// Padel-specific defaults
export const PADEL_DEFAULT_MIN_TEAMS = 4;
export const PADEL_DEFAULT_MAX_TEAMS = 16;
const PADEL_PLAYERS_PER_TEAM = 2; // Fixed for padel

/**
 * Configuration for the Padel TeamConfigStep component.
 */
export interface PadelTeamConfigStepConfig {
  allowSingles?: boolean; // If true, allows 1 player per team
}

const DEFAULT_CONFIG: PadelTeamConfigStepConfig = {
  allowSingles: false, // Padel is always doubles
};

interface PadelTeamConfigStepProps extends WizardStepProps<TeamConfigSlice> {
  config?: PadelTeamConfigStepConfig;
}

export function TeamConfigStep({
  data,
  updateData,
  draft: _draft,
  config: userConfig,
}: PadelTeamConfigStepProps) {
  const config = { ...DEFAULT_CONFIG, ...userConfig };

  // For padel, max players is always 2
  const maxPlayers = config.allowSingles ? 2 : 2;
  const minPlayers = config.allowSingles ? 1 : 2;

  // Initialize with padel-specific defaults
  useEffect(() => {
    if (!data) {
      const sport = _draft.sport?.sport ?? { uid: '', name: '' };
      const fc = _draft.fixtureConfig as {
        minCategory?: Category;
        maxCategory?: Category;
        forceSum?: boolean;
      } | null;
      const hasLegacyCategories =
        fc?.minCategory != null && fc?.maxCategory != null;
      updateData({
        gender: Gender.MALE,
        playersPerTeam: PADEL_PLAYERS_PER_TEAM,
        minAgeRestrictionEnabled: false,
        maxAgeRestrictionEnabled: false,
        ...(hasLegacyCategories
          ? {
              minCategory: fc!.minCategory!,
              maxCategory: fc!.maxCategory!,
              forceSum: fc!.forceSum ?? false,
            }
          : {
              forceSum: false,
              minCategory: {
                id: 0,
                name: '',
                minimumElo: 0,
                sport,
              },
              maxCategory: {
                id: 0,
                name: '',
                minimumElo: 0,
                sport,
              },
            }),
      });
    }
  }, [data, updateData, _draft.sport?.sport, _draft.fixtureConfig]);

  // Handle players per team change (only if singles allowed)
  const handlePlayersPerTeamChange = (value: number) => {
    const clampedValue = Math.max(minPlayers, Math.min(value, maxPlayers));
    updateData({ playersPerTeam: clampedValue });
  };

  const handleCategoryChange = (
    minCategory: Category,
    maxCategory: Category,
    forceSum: boolean,
  ) => {
    updateData({
      minCategory,
      maxCategory,
      forceSum,
    });
  };

  return (
    <div className="space-y-6">
      <GenderField
        value={data?.gender ?? Gender.MALE}
        onChange={(gender) => updateData({ gender })}
      />

      <PlayersPerTeamField
        value={data?.playersPerTeam ?? PADEL_PLAYERS_PER_TEAM}
        onChange={handlePlayersPerTeamChange}
        minPlayers={minPlayers}
        maxPlayers={maxPlayers}
        labelStyle="jugadores"
        cardTitle="Jugadores por equipo"
      />

      <AgeRestrictionField
        value={data ?? null}
        onChange={(patch) => updateData(patch)}
        helpText="Sin marcar las casillas se considera sin restricción (mínimo 0, máximo 200 en el sistema). Podés fijar una edad máxima mayor a 200 si lo necesitás."
      />

      <CategoryRangeField
        sport={_draft.sport?.sport}
        onChange={handleCategoryChange}
        initialMinCategory={data?.minCategory}
        initialMaxCategory={data?.maxCategory}
        initialForceSum={data?.forceSum}
        layout="plain"
      />
    </div>
  );
}

export default TeamConfigStep;
